import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { queryKeys } from './queryKeys';
import {
  portalRoleService,
  PortalRole,
  PortalRoleCreateData,
  PortalRoleUpdateData,
} from '@/services/portalRoleService';

// Query for portal modules list
export const usePortalModules = (params?: { include_inactive?: boolean }) => {
  return useApiQuery(
    queryKeys.portalModules.list(params),
    () => portalRoleService.getModules(params),
    {
      staleTime: 10 * 60 * 1000, // Modules rarely change, cache for 10 minutes
    }
  );
};

// Query for portal roles list
export const usePortalRoles = (params?: {
  include_system?: boolean;
  include_inactive?: boolean;
}) => {
  return useApiQuery(
    queryKeys.portalRoles.list(params),
    () => portalRoleService.getRoles(params),
    {
      staleTime: 5 * 60 * 1000, // Roles don't change often, cache for 5 minutes
    }
  );
};

// Mutation for creating a portal role
export const useCreatePortalRole = () => {
  return useApiMutation<PortalRole, unknown, PortalRoleCreateData>(
    (data) => portalRoleService.createRole(data),
    {
      invalidateKeys: [queryKeys.portalRoles.all],
      successMessage: 'Portal role created successfully',
    }
  );
};

// Mutation for updating a portal role
export const useUpdatePortalRole = () => {
  return useApiMutation<
    PortalRole,
    unknown,
    { id: string; data: PortalRoleUpdateData }
  >(({ id, data }) => portalRoleService.updateRole(id, data), {
    invalidateKeys: (_data, variables) => [
      queryKeys.portalRoles.all,
      queryKeys.portalRoles.detail(variables.id),
    ],
    successMessage: 'Portal role updated successfully',
  });
};

// Mutation for deleting a portal role
export const useDeletePortalRole = () => {
  return useApiMutation<void, unknown, string>(
    (id) => portalRoleService.deleteRole(id),
    {
      invalidateKeys: [queryKeys.portalRoles.all],
      successMessage: 'Portal role deleted successfully',
    }
  );
};

// Mutation for updating role modules (convenience wrapper)
export const useUpdatePortalRoleModules = () => {
  return useApiMutation<
    PortalRole,
    unknown,
    { roleId: string; moduleIds: string[] }
  >(
    ({ roleId, moduleIds }) =>
      portalRoleService.updateRoleModules(roleId, moduleIds),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.portalRoles.all,
        queryKeys.portalRoles.detail(variables.roleId),
      ],
      successMessage: 'Role modules updated successfully',
    }
  );
};
